import { Notification_ReadState } from '@wavingroup/iot-core/gen/ts/wavin/blaze/v1/notification_pb';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import {
  CloseIcon,
  NotificationsInboxIcon,
} from '~/shared/components/Icons/Icons';
import { useInfiniteScroll } from '~/shared/hooks/useInfiniteScroll';
import { NotificationCard } from './NotificationCard';
import { ParsedNotification } from './types';

export function NotificationList({
  isLoading,
  notifications,
  onClose,
  onMarkAsRead,
  onSeeDetails,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  filterBar,
  headerAction,
}: {
  isLoading: boolean;
  notifications: ParsedNotification[];
  onClose: () => void;
  onMarkAsRead: (name: string, readState: Notification_ReadState) => void;
  onSeeDetails: (notification: ParsedNotification) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  filterBar?: React.ReactNode;
  headerAction?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { lastItemRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: onLoadMore,
  });

  const handleSeeDetails = (notification: ParsedNotification) => {
    onMarkAsRead(notification.name, notification.readState);
    onSeeDetails(notification);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'background.paper',
          p: 6,
          py: 5,
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t('notifications.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {headerAction}
          <IconButton onClick={onClose}>
            <CloseIcon fontSize="small" sx={{ color: 'primary.dark' }} />
          </IconButton>
        </Box>
      </Box>

      {filterBar}

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && notifications.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '90%',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              gap: 2,
              px: 6,
            }}
          >
            <NotificationsInboxIcon sx={{ fontSize: 60, color: 'grey.400' }} />
            <Typography variant="h5" color="text.secondary">
              {t('notifications.noNotifications')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.noNotificationsDescription')}
            </Typography>
          </Box>
        )}

        {!isLoading &&
          notifications.map((notification, index) => (
            <Box
              key={notification.name}
              ref={index === notifications.length - 1 ? lastItemRef : undefined}
            >
              <NotificationCard
                deviceTitle={notification.deviceTitle}
                urgency={notification.urgency}
                title={notification.title}
                alertDescription={notification.alertDescription}
                type={notification.category}
                typeLabel={notification.categoryLabel}
                date={notification.date}
                resolvedDate={notification.resolvedDate}
                isResolved={notification.isResolved}
                isRead={notification.readState === Notification_ReadState.READ}
                onMarkAsRead={() =>
                  onMarkAsRead(notification.name, notification.readState)
                }
                onSeeDetails={() => handleSeeDetails(notification)}
              />
            </Box>
          ))}

        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>
    </>
  );
}
